import { css, useTheme } from '@emotion/react';
import { ArrowLeft, AudioLines, PanelLeftClose, PanelLeftOpen, QrCode, Save } from 'lucide-react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';

import { fontFamilies, layout, mq, radii, spacing } from '../../theme/tokens';

import { ProfilePopover } from './profile-popover';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

function NavItem({
  icon,
  label,
  to,
  collapsed,
  onClick,
  noActiveStyle = false,
}: {
  icon: React.ReactNode;
  label: string;
  to: string;
  collapsed: boolean;
  onClick?: () => void;
  noActiveStyle?: boolean;
}) {
  const theme = useTheme();

  const itemCss = css`
    display: flex;
    align-items: center;
    gap: ${spacing.sm};
    padding: ${spacing.sm} ${collapsed ? spacing.sm : spacing.md};
    border-radius: ${radii.sm};
    color: ${theme.colors.sidebarText};
    text-transform: uppercase;
    letter-spacing: 0.2px;
    transition:
      background 0.15s,
      color 0.15s;
    cursor: pointer;
    justify-content: ${collapsed ? 'center' : 'flex-start'};
    width: 100%;
    overflow: hidden;
    white-space: nowrap;

    &:hover {
      background: ${theme.colors.sidebarHoverBg};
    }

    ${!noActiveStyle &&
    `&.active {
      color: ${theme.colors.sidebarActiveText};
      background: ${theme.colors.sidebarActiveBg};
      font-weight: 600;
    }`}
  `;

  return (
    <NavLink to={to} onClick={onClick} title={collapsed ? label : undefined} css={itemCss}>
      {icon}
      {collapsed ? <span className="sr-only">{label}</span> : <span>{label}</span>}
    </NavLink>
  );
}

export function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const theme = useTheme();
  const navigate = useNavigate();

  const isEditorRoute = useMatch('/app/projects/:id/edit');
  const projectId = isEditorRoute?.params.id;

  const sidebarWidth = collapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth;

  return (
    <>
      {isOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close navigation"
          data-testid="sidebar-overlay"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onClose();
          }}
          css={css`
            position: fixed;
            inset: 0;
            background: ${theme.colors.overlay};
            z-index: 40;
            ${mq.lg} {
              display: none;
            }
          `}
        />
      )}
      <nav
        aria-label="Main navigation"
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: ${sidebarWidth};
          background: ${theme.colors.sidebarBg};
          transform: translateX(${isOpen ? '0' : '-100%'});
          transition:
            transform 0.2s ease,
            width 0.2s ease;
          z-index: 50;
          display: flex;
          flex-direction: column;
          overflow: hidden;

          ${mq.lg} {
            transform: none;
          }
        `}
      >
        {/* Header */}
        <div
          css={css`
            padding: ${spacing.lg} ${collapsed ? spacing.sm : spacing.md};
            display: flex;
            align-items: center;
            gap: ${spacing.sm};
            justify-content: ${collapsed ? 'center' : 'flex-start'};
            flex-shrink: 0;
          `}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="2" y="6" width="4" height="12" rx="1" fill={theme.colors.primary} />
            <rect
              x="8"
              y="3"
              width="4"
              height="18"
              rx="1"
              fill={theme.colors.primary}
              opacity="0.7"
            />
            <rect
              x="14"
              y="8"
              width="4"
              height="8"
              rx="1"
              fill={theme.colors.primary}
              opacity="0.5"
            />
            <rect
              x="20"
              y="5"
              width="4"
              height="14"
              rx="1"
              fill={theme.colors.primary}
              opacity="0.3"
            />
          </svg>
          {!collapsed && (
            <span
              css={css`
                font-family: ${fontFamilies.display};
                font-size: 1.125rem;
                font-weight: 700;
                color: ${theme.colors.text};
                white-space: nowrap;
              `}
            >
              Sonematic
            </span>
          )}
        </div>

        {/* Nav items — editor context only */}
        <ul
          css={css`
            padding: 0 ${collapsed ? spacing.xs : spacing.sm};
            display: flex;
            flex-direction: column;
            gap: ${spacing.xs};
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
          `}
        >
          {projectId && (
            <>
              <li>
                <NavItem
                  icon={<ArrowLeft size={18} aria-hidden="true" />}
                  label="Back to Scripts"
                  to="/app/projects"
                  collapsed={collapsed}
                  onClick={onClose}
                  noActiveStyle
                />
              </li>
              <li
                role="separator"
                css={css`
                  border-top: 1px solid ${theme.colors.border};
                  margin: ${spacing.xs} 0;
                `}
              />
              <li>
                <NavItem
                  icon={<Save size={18} aria-hidden="true" />}
                  label="Save"
                  to={`/app/projects/${projectId}/edit`}
                  collapsed={collapsed}
                  noActiveStyle
                />
              </li>
              <li>
                <NavItem
                  icon={<AudioLines size={18} aria-hidden="true" />}
                  label="Rehearsal Mode"
                  to={`/app/projects/${projectId}/rehearse`}
                  collapsed={collapsed}
                  onClick={onClose}
                  noActiveStyle
                />
              </li>
              <li>
                <button
                  onClick={() => {
                    if (projectId) {
                      navigate(`/app/projects/${projectId}/edit?connect=true`);
                      onClose?.();
                    }
                  }}
                  title={collapsed ? 'Connect Device' : undefined}
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: ${spacing.sm};
                    padding: ${spacing.sm} ${collapsed ? spacing.sm : spacing.md};
                    border-radius: ${radii.sm};
                    color: ${theme.colors.sidebarText};
                    text-transform: uppercase;
                    letter-spacing: 0.2px;
                    transition:
                      background 0.15s,
                      color 0.15s;
                    cursor: pointer;
                    justify-content: ${collapsed ? 'center' : 'flex-start'};
                    width: 100%;
                    overflow: hidden;
                    white-space: nowrap;
                    background: none;
                    border: none;
                    font: inherit;

                    &:hover {
                      background: ${theme.colors.sidebarHoverBg};
                    }
                  `}
                >
                  <QrCode size={18} aria-hidden="true" />
                  {collapsed ? (
                    <span className="sr-only">Connect Device</span>
                  ) : (
                    <span>Connect Device</span>
                  )}
                </button>
              </li>
            </>
          )}
        </ul>

        {/* Footer */}
        <div
          css={css`
            padding: ${spacing.sm} ${collapsed ? spacing.sm : spacing.md} ${spacing.md};
            border-top: 1px solid ${theme.colors.border};
            display: flex;
            flex-direction: column;
            gap: ${spacing.sm};
            flex-shrink: 0;
            margin-top: auto;
          `}
        >
          <ProfilePopover collapsed={collapsed} />

          <button
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={onToggleCollapse}
            css={css`
              display: flex;
              align-items: center;
              justify-content: ${collapsed ? 'center' : 'flex-start'};
              gap: ${spacing.sm};
              padding: ${spacing.xs} ${spacing.sm};
              border-radius: ${radii.sm};
              color: ${theme.colors.sidebarText};
              font-size: 0.8125rem;
              cursor: pointer;
              transition: background 0.15s;
              width: 100%;

              &:hover {
                background: ${theme.colors.sidebarHoverBg};
              }
            `}
          >
            {collapsed ? (
              <PanelLeftOpen size={20} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={20} aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>
    </>
  );
}
